import * as logger from "../utils/logger";
import Api from "../services/api";
import { CancelablePromise } from "../utils/cancelablePromise";
import { Device } from "../model/Device";
import { Trailer } from "../model/Trailer";
import { getCurrentVehicle } from "../utils/device";
import { TrailerAttachments } from "../model/TrailerAttachments";
import { makeDeviceCache, makeDefectListCache, makeTrailerCache } from "../utils/storage/cache";
import { SecurityService } from "../utils/security";
import { IDefect, TDefectAssetType } from "../model/Defect";
import { DriveStore } from "../store/reducers";
import { DeviceDefectListActions, TrailerDefectListActions } from "../store/defectLists/actions";
import { extend } from "../utils/vanilla";
import { usesTrailerDeviceIdentity } from "./trailerAttachmentDeviceLink";

export interface IMiscSync {
    syncDefectLists(force?: boolean, device?: Device, trailers?: Trailer[]): CancelablePromise<IDefectListMeta[]>;
}

interface IDefectListMeta {
    assetType: TDefectAssetType;
    entityId: string;
    defectLists: IDefect[];
}

function getUserId(api: Api) {
    let apiUser = api.getUser();
    return apiUser ? apiUser.id : undefined;
}

function downloadDefectLists(api: Api, store: DriveStore, assetType: TDefectAssetType, entity: IGroupLinkedEntity): CancelablePromise<IDefectListMeta> {
    let apiUser = api.getUser();
    let search: any = {};
    // MYG-148762: under DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE a trailer is its own Device.
    if (assetType === "Device" || usesTrailerDeviceIdentity(store)) {
        search.deviceSearch = {id: entity.id};
    } else {
        search.trailerSearch = {id: entity.id};
    }
    return api.callPromise("Get", {
        typeName: "Defect",
        search
    })
    .then(defectLists => {
        let returnableDefectLists = [];
        if (defectLists) {
            // Keep archived lists cached: carry-over defects from an archived list still need
            // to resolve their name/severity from here. Archived lists are excluded from
            // *selection* (new-inspection dropdown) in inspect.ts, not from this cache.
            returnableDefectLists = defectLists.filter(defectList => defectList.assetType === assetType || defectList.assetType === "All");
            logger.log(logger.Source.SYNC, `Downloaded ${ returnableDefectLists.length } defect list(s) for ${ assetType.toLowerCase() } '${ entity.name }'`, apiUser);
        }
        return {
            assetType,
            entityId: entity.id,
            defectLists: returnableDefectLists
        } as IDefectListMeta;
    });
}

function getCachedEntityDefectListMeta(api: Api, store: DriveStore, assetType: TDefectAssetType, entity: IIdEntity): CancelablePromise<IDefectListMeta> {
    let apiUserId = getUserId(api);
    let cache = assetType === "Device" ? makeDeviceCache(store) : makeTrailerCache(store);
    return cache.getOrFetch(api, entity, apiUserId).then(populatedEntities => {
        if (populatedEntities && populatedEntities.length && populatedEntities[0].length) {
            return downloadDefectLists(api, store, assetType, populatedEntities[0][0]);
        }
        return CancelablePromise.resolve<IDefectListMeta>();
    });
}

export function syncDefectLists(securityService: SecurityService, api: Api, store: DriveStore, force = false, device?: Device, trailers?: Trailer[]): CancelablePromise<IDefectListMeta[]> {
    let apiUser = api.getUser();
    const defectListCache = makeDefectListCache(store);
    let apiUserId = getUserId(api);
    if (!apiUserId || !securityService.can("viewDvir", apiUserId)) {
        return CancelablePromise.resolve();
    }
    let currentDevice = device || getCurrentVehicle() as Device;
    let currentTrailers = trailers;
    if (!currentTrailers) {
        let trailerAttachmentCollection = new TrailerAttachments(api, store);
        currentTrailers = trailerAttachmentCollection.getCurrentTrailers();
    }

    let promises: CancelablePromise<IDefectListMeta>[] = [];

    let deviceDefectLists = {};
    let trailerDefectLists = {};

    if (!force) {
        deviceDefectLists = extend(true, {}, store.getState().deviceDefectLists[apiUserId] || {}) as IIdDictionary<IDefect[]>;
        trailerDefectLists = extend(true, {}, store.getState().trailerDefectLists[apiUserId] || {}) as IIdDictionary<IDefect[]>;
    }

    if (currentDevice) {
        if (currentDevice.id === "NoDeviceId") {
            // Don't download defect lists if we're not using a device
            return CancelablePromise.resolve([]);
        }

        if (!deviceDefectLists || !(currentDevice.id in deviceDefectLists)) {
            promises.push(getCachedEntityDefectListMeta(api, store, "Device", currentDevice));
        }
    }
    if (currentTrailers && currentTrailers.length > 0) {
        currentTrailers.forEach(trailer => {
            if (!trailerDefectLists || !(trailer.id in trailerDefectLists)) {
                promises.push(getCachedEntityDefectListMeta(api, store, "Trailer", trailer));
            }
        });
    }
    return CancelablePromise.all(promises)
        .then(defectLists => {
            if (!defectLists || !defectLists.length || !apiUserId) {
                return [];
            }
            defectListCache.add(([] as (IDefect)[]).concat.apply([], defectLists.map(defectList => defectList.defectLists)), apiUserId);
            deviceDefectLists = extend(true, {}, store.getState().deviceDefectLists[apiUserId] || {}) as IIdDictionary<IDefect[]>;
            trailerDefectLists = extend(true, {}, store.getState().trailerDefectLists[apiUserId] || {}) as IIdDictionary<IDefect[]>;
            defectLists.forEach(defectListWithMetadata => {
                let isDeviceDefectList = defectListWithMetadata.assetType === "Device";
                (isDeviceDefectList ? deviceDefectLists : trailerDefectLists)[defectListWithMetadata.entityId] = defectListWithMetadata.defectLists.map(defectList => ({id: defectList.id}));
            });
            store.dispatch(DeviceDefectListActions.setDeviceDefectLists({
                userId: apiUserId,
                defectLists: deviceDefectLists
            }));
            store.dispatch(TrailerDefectListActions.setTrailerDefectLists({
                userId: apiUserId,
                defectLists: trailerDefectLists
            }));
            return defectLists;
        })
        .catch(err => {
            logger.error(logger.Source.SYNC, `Defect Lists: ${ err.message }`, apiUser, err);
            return [];
        });
}
